import React, { useState, useEffect, useRef, useContext } from 'react'

import {
    useNavigate,
    useParams,
    useSearchParams,
    useLocation,
} from 'react-router-dom'
import {
    PurchaseStage,
    Attachment,
    CurrencyList,
    PurchaseCountAndSum,
    PurchaseSearch,
    ImportMessage,
    PurchaseDataReestr,
} from './types'

import { useAuth } from '../../../hooks/auth'
import { Structure } from '../Org/Structure/types'
import { OrganizationInfo } from '../Org/Home/types'

import Checkbox from '../../../components/form/Checkbox'

// import './style.css'
// import '../../../components/Toolbar/Icon.css'

import PurchaseHeader from './components/Header'
import PurchaseStageTab from './components/Tabs/PurchaseStageTab'
import PurchasesTableRow from './components/TableRow'
import LoadingIcon from '../../../components/LoadingIcon'
import TableStageToolbarButtons from './components/Buttons/TableStageToolbarButtons'
import TableStageToolbarDownButtons from './components/Buttons/TableStageToolbarDownButton'
import TableRightClickButton from './components/Buttons/TableRightClickButton'

import TableHeader from './components/TableHeader/TableHeader'

import {
    UserFilter,
    FilterSetting,
    Criteria,
} from './components/TableHeader/FilterTypes'

import FileUploaderBlock from './components/FileUploaderBlock'
import { Slide, ToastContainer } from 'react-toastify'

import { ThemeContext } from '../../../components/ThemeContext'

import HoverTooltip from '../../../components/HoverTooltip'

const REGISTRY_STAGE = 'Реестр закупок'

type RowStatus = 'idle' | 'pending' | 'success' | 'error' | 'error_back'

type ApiResponseRoleGuid = {
    result: QueryItem[]
}

type QueryItem = {
    guid: string
    name: string
    organization_guid: string
    created_at: string
    updated_at: string
}

const getCurrentDate = (): string => {
    const today = new Date()
    const year = today.getFullYear()
    const month = String(today.getMonth() + 1).padStart(2, '0') // добавляем 1, так как месяцы начинаются с 0
    const day = String(today.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

const getStructureList = async (): Promise<Structure[]> => {
    return fetch(
        process.env.REACT_APP_API_URL + '/api/backend/v1/organization_struct',
        {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        }
    )
        .then((response) => {
            if (response.ok) {
                return response.json()
            } else {
                throw new Error('Ошибка запроса на способы закупок')
            }
        })
        .catch((e) => {
            console.error(e)
        })
}

const getCurrencyList = async (): Promise<CurrencyList[]> => {
    return fetch(process.env.REACT_APP_API_URL + '/api/backend/v1/currency', {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    })
        .then((response) => {
            if (response.ok) {
                return response.json()
            } else {
                throw new Error('Ошибка запроса на курсы валют')
            }
        })
        .catch((e) => {
            console.error(e)
        })
}

const getRole = async (role_guid: string): Promise<QueryItem> => {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/backend/v1/organizations/roles_by_guid/${role_guid}`,
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )

        if (!response.ok) {
            throw new Error('Ошибка запроса на роль')
        }

        const data: ApiResponseRoleGuid = await response.json()
        // Извлекаем первый элемент массива, т.к. он всегда содержит единственный объект
        return data.result[0]
    } catch (error) {
        console.error('Ошибка:', error)
        // Возвращаем объект по умолчанию, соответствующий типу QueryItem
        return {
            guid: '',
            name: '',
            organization_guid: '',
            created_at: '',
            updated_at: '',
        }
    }
}

// Отдельная функция для проверки, есть ли скролл
const checkScroll = (element: HTMLElement | null): boolean => {
    ;``
    return element ? element.scrollHeight > element.clientHeight : false
}

const PurchaseListPage: React.FC = () => {
    const navigate = useNavigate()
    const location = useLocation()

    const { theme } = useContext(ThemeContext)

    const { user } = useAuth()

    const [orgData, setOrgData] = useState<OrganizationInfo>()

    const [filter, setFilter] = useState<UserFilter | null>(null)
    // Дополнительное состояние
    const [filterLoaded, setFilterLoaded] = useState(false)

    const [structureList, setStructureList] = useState<Structure[]>([])
    const [currencyList, setCurrencyList] = useState<CurrencyList[]>([])

    const [isUserRoleName, setUserRoleName] = useState('')

    const [searchParams] = useSearchParams()
    const initialStage = searchParams.get('stage') as PurchaseStage
    const initialYearPeriod = Number(searchParams.get('year'))

    const [stage, setStage] = useState<PurchaseStage>(
        initialStage || PurchaseStage.ALL
    )

    const [yearPeriod, setYearPeriod] = useState<number | null>(
        initialYearPeriod || new Date().getFullYear()
    )

    const [editStage, setEditStage] = useState(null)
    const [readyStage, setReadyStage] = useState(null)
    const [sendStage, setSendStage] = useState(null)
    const [perfStage, setPerfStage] = useState(null)
    const [closeStage, setCloseStage] = useState(null)
    const [allStage, setAllStage] = useState(null)

    const [purchaseCountAndSum, setPurchaseCountAndSum] =
        useState<PurchaseCountAndSum | null>(null)
    const [loadingCountSum, setLoadingCountSum] = useState(false)

    const [isLoading, setLoading] = useState<boolean>(true)
    const [items, setItems] = useState<PurchaseDataReestr[]>([])
    const [page, setPage] = useState(1) // -1 - no more data

    const [selectedItems, setSelectedItems] = useState<string[]>([])
    const [selectAll, setSelectAll] = useState<boolean>(false)

    const [massImportMode, setMassImportMode] = useState<boolean>(false)

    const [purchaseRegNumber, setPurchaseRegNumber] = useState('')
    const [singlePurchaseImportStatus, setSinglePurchaseImportStatus] =
        useState<boolean | null>(false)
    const [isSinglePurchaseImporting, setIsSinglePurchaseImporting] =
        useState<boolean>(false)

    const [isSearchingPurchaseFromEis, setSearchingPurchaseFromEis] =
        useState<boolean>(false)
    const [serachPurchaseFromEisData, setPurchaseFromEisData] =
        useState<PurchaseSearch | null>(null)
    const [searchFromDate, setSearchFromDate] = useState('')
    const [searchToDate, setSearchToDate] = useState('')

    const [importFromEisPopup, setImportFromEisPopup] = useState<boolean>(false)
    const [isImportingFromEis, setImportingFromEis] = useState<boolean>(false)
    const [importMessage, setImportMessage] = useState<ImportMessage | null>(
        null
    )
    const [clickUpdateButton, setClickUpdateButton] = useState<boolean>(false)

    const [rowLoadStatus, setRowLoadStatus] = useState<
        Record<string, RowStatus>
    >({})
    const [rowLoadError, setRowLoadError] = useState<
        Record<string, string | undefined>
    >({})
    const abortImportRef = useRef<AbortController | null>(null)

    const importPopupAbortRef = useRef<AbortController | null>(null)

    const [deletePurchasePopup, setDeletePurchasePopup] =
        useState<boolean>(false)
    const [isDeleting, setDeleting] = useState<boolean>(false)
    const [deleteItems, setDeleteItems] = useState<PurchaseDataReestr[]>([])

    const [menuVisible, setMenuVisible] = useState(false)
    const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 })

    const [uploadMessage, setUploadMessage] = useState<string | null>(null) // Сообщение о результате загрузки

    //  проверка скрол есть или нет
    const scrollRef = useRef<HTMLDivElement>(null)
    const [hasScroll, setHasScroll] = useState(false)

    const [updateItems, setUpdateItems] = useState<PurchaseDataReestr[]>([])
    const [updatePurchasePopup, setUpdatePurchasePopup] = useState(false)

    const loadOrganizationData = async (): Promise<OrganizationInfo> => {
        return fetch(
            process.env.REACT_APP_API_URL + '/api/backend/v1/organization',
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Failed to fetch organization')
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }

    // Функция для получения фильтров пользователя
    const getUserFilterTypeCategory = async (
        user_guid: string,
        filter_type: string,
        filter_category: string
    ): Promise<UserFilter> => {
        return fetch(
            process.env.REACT_APP_API_URL +
                `/api/backend/v1/user_and_filtertype?user_guid=${user_guid}&filter_type=${filter_type}&filter_category=${filter_category}`,
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Ошибка запроса на фильтр пользователя')
                }
            })
            .then((data) => {
                // Если данные представляют собой массив, возвращаем первую позицию (либо null, если массив пуст)
                if (Array.isArray(data) && data.length > 0) {
                    return data[0]
                } else {
                    return null
                }
            })
            .catch((error) => {
                console.error(error)
                throw error
            })
    }

    // Функция для обновления фильтра пользователя по PUT-запросу
    const updateUserFilter = async (
        filterData: {
            filter_type: string
            filter_settings: FilterSetting
            filter_category: string
        },
        guid: string
    ): Promise<UserFilter> => {
        return fetch(
            `${process.env.REACT_APP_API_URL}/api/backend/v1/user_filter/${guid}`,
            {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    accept: 'application/json',
                },
                body: JSON.stringify(filterData),
            }
        )
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error(
                        'Ошибка запроса на обновление фильтра пользователя'
                    )
                }
            })
            .catch((error) => {
                console.error(error)
                throw error
            })
    }

    const createUserFilter = async (filterData: {
        filter_type: string
        filter_settings: { criteria: Criteria | null; filter_enabled: boolean }
        filter_category: string
    }): Promise<UserFilter> => {
        return fetch(
            process.env.REACT_APP_API_URL + '/api/backend/v1/user_filter',
            {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    accept: 'application/json',
                },
                body: JSON.stringify(filterData),
            }
        )
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Ошибка создания фильтра пользователя')
                }
            })
            .catch((error) => {
                console.error(error)
                throw error
            })
    }

    const loadPurchases = (
        page_num = 1,
        userFilter: UserFilter | null = null
    ) => {
        setLoading(true)

        const pageSize = 100
        const postfix = 'purchase_with_approval_and_user_reestr_data'

        // аккуратно собираем query
        const params = new URLSearchParams()
        if (typeof yearPeriod === 'number') {
            params.set('year', String(yearPeriod))
        } else {
            // на случай переиспользования params (не обязателен, но безопасно)
            params.delete('year')
        }
        params.set('stage', stage)
        params.set('page', String(page_num))
        params.set('size', String(pageSize))
        if (userFilter?.filter_settings?.filter_enabled) {
            params.set(
                'filter_settings',
                JSON.stringify(userFilter.filter_settings)
            )
        }

        const url = `${process.env.REACT_APP_API_URL}/api/backend/v1/${postfix}?${params.toString()}`

        fetch(url, {
            method: 'GET',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
        })
            .then((response) => {
                if (response.ok) return response.json()
                throw new Error(
                    'Failed to fetch purchases (page: ' + page_num + ')'
                )
            })
            .catch((e) => {
                console.error(e)
                return []
            })
            .then((data) => {
                const shouldFilter = stage && stage !== REGISTRY_STAGE
                const filteredData = shouldFilter
                    ? data.filter(
                          (item: PurchaseDataReestr) =>
                              item.purchase_stage === stage
                      )
                    : data

                if (page_num === 1) {
                    setItems(filteredData)
                } else {
                    setItems((prev) => [...prev, ...filteredData])
                }
                setPage(filteredData.length < pageSize ? -1 : page_num + 1)
            })
            .finally(() => setLoading(false))
    }

    useEffect(() => {
        // Синхронизируйте локальное состояние с маршрутом

        const fetchData = async () => {
            const organization = await loadOrganizationData()
            if (organization) {
                setOrgData(organization)
            }

            const role = await getRole(user?.object.role_id)
            setUserRoleName(role.name)

            const structs = await getStructureList()
            if (structs) {
                setStructureList(structs) // Обновляем список структур
            }

            const currency = await getCurrencyList()
            if (currency) {
                setCurrencyList(currency) // Обновляем список структур
            }
        }
        fetchData()
    }, [])

    // перенаправление при нажатии на tab-stage и обнудение select при изменении stage
    useEffect(() => {
        setSelectedItems([])
        navigate(`/hub/purchases?year=${yearPeriod}&stage=${stage}`)

        // Загрузка фильтра при изменении stage
        setLoading(true)
        setFilterLoaded(false)

        getUserFilterTypeCategory(user?.object.user_id, stage, 'Закупка')
            .then((data) => {
                setFilter(data)
                setFilterLoaded(true) // фильтр загружен успешно
            })
            .catch((error) => {
                console.error(error)
                setFilter(null) // Если ошибка – отключаем фильтр
                setFilterLoaded(true) // даже при ошибке ставим true, иначе зависнем
            })
    }, [stage])

    // Проверка скролла
    useEffect(() => {
        const updateScrollState = () => {
            setHasScroll(checkScroll(scrollRef.current))
        }

        // Первоначальная проверка
        updateScrollState()

        // Перепроверяем при изменении размеров окна
        window.addEventListener('resize', updateScrollState)
        return () => window.removeEventListener('resize', updateScrollState)
    }, [items])

    // Загрузка позиций при загрузке фильтра
    useEffect(() => {
        if (!filterLoaded) return // Ждём завершение загрузки фильтра

        loadPurchases(1, filter)

        setSelectedItems([])
        setSelectAll(false)
    }, [filterLoaded, filter])

    // При изменении yearPeriod
    useEffect(() => {
        setSelectedItems([])
        loadPurchases(1, filter)
    }, [yearPeriod])

    // Функция для обработки изменения состояния отдельного checkbox
    const handleCheckboxChange = (purchase_reg_number: string) => {
        setSelectedItems((prevSelectedItems) => {
            if (prevSelectedItems.includes(purchase_reg_number)) {
                return prevSelectedItems.filter(
                    (itemRegNumber) => itemRegNumber !== purchase_reg_number
                )
            } else {
                return [...prevSelectedItems, purchase_reg_number]
            }
        })
        console.log(selectedItems)
    }

    // Функция для обработки изменения состояния отдельного checkbox при нажатии правой кнопкой
    const handleRightClickCheckboxChange = (purchase_reg_number: string) => {
        setSelectedItems((prevSelectedItems) => {
            if (prevSelectedItems.includes(purchase_reg_number)) {
                return prevSelectedItems // Если уже выбран, ничего не меняем
            }
            return [...prevSelectedItems, purchase_reg_number] // Добавляем в выбранные
        })
    }

    // Функция для обработки изменения состояния "Select All"
    const handleSelectAllChange = () => {
        setSelectAll(!selectAll)
        if (!selectAll) {
            // Выбираем только те элементы, которые соответствуют текущей стадии
            const filteredItems =
                stage == 'Реестр закупок'
                    ? items.map((item) => String(item.registration_number))
                    : items
                          .filter((item) => item.purchase_stage === stage)
                          .map((item) => String(item.registration_number))

            setSelectedItems(filteredItems)
        } else {
            // Сбрасываем выбор всех элементов
            setSelectedItems([])
        }
        console.log(selectedItems)
    }

    // Закрыть меню при клике вне его
    useEffect(() => {
        const handleOutsideClick = () => setMenuVisible(false)
        window.addEventListener('click', handleOutsideClick)

        return () => window.removeEventListener('click', handleOutsideClick)
    }, [])

    const handleContextMenu = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault() // Отключаем стандартное контекстное меню
        setMenuPosition({ x: e.pageX, y: e.pageY }) // Устанавливаем позицию меню
        setMenuVisible(true) // Показываем меню
    }

    // Отдельный эффект на изменение filter:
    useEffect(() => {
        if (filter && filter?.filter_settings?.filter_enabled) {
            loadPurchases(1, filter)
        } else if (filter) {
            loadPurchases() // Если фильтр есть, но он отключен
        }
    }, [filter]) // <-- Запускается только после обновления filter

    const handleFilterChange = async (currentFilter?: UserFilter) => {
        const filterToUse = currentFilter || filter
        if (filterToUse) {
            updateUserFilter(
                {
                    filter_type: stage,
                    filter_settings: filterToUse.filter_settings,
                    filter_category: 'Закупка',
                },
                filterToUse.guid
            ).catch(console.error)
        }
    }

    const handleFilterErase = async () => {
        if (filter) {
            try {
                const updatedFilter = await updateUserFilter(
                    {
                        filter_type: stage,
                        filter_settings: {
                            criteria: null,
                            filter_enabled: true,
                        },
                        filter_category: 'Закупка',
                    },
                    filter.guid
                )
                setFilter(updatedFilter)
                loadPurchases(1, filter)
            } catch (error) {
                console.error('Ошибка обновления фильтра:', error)
            }
        }
    }

    // Функция, вызываемая по нажатию на кнопку
    const handleFilter = async () => {
        if (!filter) {
            try {
                const newFilter = await createUserFilter({
                    filter_type: stage,
                    filter_settings: {
                        criteria: null,
                        filter_enabled: true,
                    },
                    filter_category: 'Закупка',
                })
                setFilter(newFilter)
                await loadPurchases(1, newFilter) // Используем новый фильтр явно
            } catch (error) {
                console.error('Ошибка создания фильтра:', error)
            }
        } else {
            const currentSettings = filter.filter_settings || {
                filter_enabled: false,
                criteria: {} as Criteria,
            }

            const newFilter: UserFilter = {
                ...filter,
                filter_settings: {
                    ...currentSettings,
                    filter_enabled: !currentSettings.filter_enabled,
                },
            }

            try {
                const updatedFilter = await updateUserFilter(
                    {
                        filter_type: newFilter.filter_type,
                        filter_settings: newFilter.filter_settings,
                        filter_category: 'Закупка',
                    },
                    newFilter.guid
                )
                setFilter(updatedFilter)
                await loadPurchases(1, updatedFilter) // <-- тут изменено
            } catch (error) {
                console.error('Ошибка обновления фильтра:', error)
            }
        }
    }

    const abortImportPopupRequests = () => {
        if (importPopupAbortRef.current) {
            importPopupAbortRef.current.abort()
            importPopupAbortRef.current = null
        }

        setImportingFromEis(false)
        setIsSinglePurchaseImporting(false)
        setSearchingPurchaseFromEis(false)
    }

    const handleImportFromEisPopup = () => {
        const isClosing = importFromEisPopup === true

        if (isClosing) {
            abortImportPopupRequests()

            loadPurchases(1, filter)
        }

        setImportFromEisPopup(!importFromEisPopup)
        setPurchaseFromEisData(null)
        setRowLoadStatus({})
        setRowLoadError({})
        setSinglePurchaseImportStatus(null)
        setClickUpdateButton(false)
    }

    const searchPurchaseFromEis = async (
        date_from: string,
        date_to: string,
        signal?: AbortSignal
    ) => {
        setClickUpdateButton(false)
        setSearchingPurchaseFromEis(true)
        setPurchaseFromEisData(null)

        const url = new URL(
            `${process.env.REACT_APP_API_URL}/api/backend/v1/purchases_from_eis_by_inn`
        )
        url.searchParams.set('inn', String(orgData?.inn))
        url.searchParams.set('date_from', String(date_from))
        url.searchParams.set('date_to', String(date_to))

        try {
            const response = await fetch(url.toString(), {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                signal,
            })

            if (!response.ok) {
                setPurchaseFromEisData(null)
                throw new Error(
                    `Error: ${response.status} ${response.statusText}`
                )
            }

            const json: PurchaseSearch = await response.json()
            setPurchaseFromEisData(json)
        } catch (e: any) {
            if (e?.name === 'AbortError') {
                console.log('Поиск закупок из ЕИС отменен')
                return
            }

            console.error('Error fetching purchases from EIS:', e)
            setPurchaseFromEisData(null)
        } finally {
            setSearchingPurchaseFromEis(false)
        }
    }

    const importPurchaseFromEis = async (
        purchase_reg_number: string,
        signal?: AbortSignal,
        purchase_stage?: string
    ): Promise<{
        ok: boolean
        purchase?: PurchaseDataReestr
        error?: string
    }> => {
        setIsSinglePurchaseImporting(true)
        setClickUpdateButton(true)

        const url = new URL(
            `${process.env.REACT_APP_API_URL}/api/backend/v1/purchases/only_load_purchase_from_eis`
        )
        url.searchParams.set('reg_number', String(purchase_reg_number))

        if (purchase_stage) {
            url.searchParams.set('stage', String(purchase_stage))
        }

        try {
            const response = await fetch(url.toString(), {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                signal,
            })

            // Бэкенд может вернуть и 200, и не-200. Пытаемся прочитать JSON в любом случае.
            let payload: any = null
            try {
                payload = await response.json()
            } catch {
                // пустой ответ или не JSON
            }

            // Если пришло поле message — считаем это ошибкой (по ТЗ)
            if (payload?.message) {
                const detailMsg =
                    payload?.details || payload?.message || 'Ошибка импорта'
                setSinglePurchaseImportStatus(false)
                setIsSinglePurchaseImporting(false)
                setRowLoadError((prev) => ({
                    ...prev,
                    [purchase_reg_number]: detailMsg,
                }))
                return { ok: false, error: detailMsg }
            }

            // Если не ОК по HTTP, но и без message — тоже как ошибка
            if (!response.ok) {
                setSinglePurchaseImportStatus(false)
                setIsSinglePurchaseImporting(false)
                setRowLoadError((prev) => ({
                    ...prev,
                    [purchase_reg_number]: `HTTP ${response.status} ${response.statusText}`,
                }))
                return {
                    ok: false,
                    error: `HTTP ${response.status} ${response.statusText}`,
                }
            }

            // Успех — вернули сам договор
            setSinglePurchaseImportStatus(true)
            setIsSinglePurchaseImporting(false)
            setRowLoadError({})
            return { ok: true, purchase: payload as PurchaseDataReestr }
        } catch (e: any) {
            // Закрыть — это не ошибка бизнеса, вернем понятное сообщение
            if (e?.name === 'AbortError') {
                setSinglePurchaseImportStatus(null)
                setIsSinglePurchaseImporting(false)
                return { ok: false, error: 'Операция отменена' }
            }
            setSinglePurchaseImportStatus(false)
            setIsSinglePurchaseImporting(false)
            setRowLoadError((prev) => ({
                ...prev,
                [purchase_reg_number]: String(e),
            }))
            return { ok: false, error: String(e) }
        }
    }

    const importAllFoundPurchases = async () => {
        setClickUpdateButton(true)
        if (!serachPurchaseFromEisData?.data?.length) return

        setImportingFromEis(true)

        const controller = new AbortController()
        importPopupAbortRef.current = controller

        try {
            for (const row of serachPurchaseFromEisData.data) {
                if (controller.signal.aborted) break

                const key = String(row.purchase_reg_number)

                const purchase_stage = String(row.stage)

                setRowLoadStatus((prev) => ({ ...prev, [key]: 'pending' }))
                setRowLoadError((prev) => ({ ...prev, [key]: undefined }))

                const res = await importPurchaseFromEis(
                    key,
                    controller.signal,
                    purchase_stage
                )

                if (controller.signal.aborted) break

                if (res.ok) {
                    setRowLoadStatus((prev) => ({
                        ...prev,
                        [key]: 'success',
                    }))
                } else {
                    setRowLoadStatus((prev) => ({ ...prev, [key]: 'error' }))
                    setRowLoadError((prev) => ({ ...prev, [key]: res.error }))
                }
            }
        } finally {
            setImportingFromEis(false)
            importPopupAbortRef.current = null

            if (!controller.signal.aborted) {
                loadPurchases(1, filter)
            }
        }
    }

    const deletePurchase = async (
        purchase_reg_number: string
    ): Promise<void> => {
        const url =
            process.env.REACT_APP_API_URL +
            `/api/backend/v1/purchases/purchase_reg_number/${purchase_reg_number}`

        try {
            const response = await fetch(url, {
                method: 'DELETE',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            })

            if (!response.ok) {
                throw new Error(
                    `Failed to delete purchase. Status: ${response.status}`
                )
            }

            console.log('Purchase deleted successfully')
        } catch (error) {
            console.error('Error while deleting purchase:', error)
            setDeleting(false)
        } finally {
        }
    }

    const handleDeletePurchasePopup = () => {
        if (deletePurchasePopup == false) {
            const filteredData = items.filter((item) =>
                selectedItems.includes(String(item.registration_number))
            )
            setDeleteItems(filteredData)
        }
        setDeletePurchasePopup(!deletePurchasePopup)
    }

    const handleDeleteClick = async () => {
        setDeleting(true)

        let remainingDeleteItems = [...selectedItems] // Копируем массив, чтобы избежать изменения оригинала.

        for (const item of selectedItems) {
            await deletePurchase(item) // Отправляем запрос на удаление.
            // Уменьшаем массив, убирая удалённый элемент.
            remainingDeleteItems = remainingDeleteItems.filter(
                (i) => i !== item
            )
            const filteredDeleteItems = deleteItems.filter((delItem) =>
                remainingDeleteItems.includes(
                    String(delItem.registration_number)
                )
            )
            setDeleteItems(filteredDeleteItems)
        }
        setSelectedItems([])
        setSelectAll(false)
        setDeleting(false)
        setDeletePurchasePopup(false)
        loadPurchases(1, filter)
    }

    const handleDeleteItem = (itemRegNumber: string) => {
        setDeleteItems((prevItems) =>
            prevItems.filter(
                (item) => String(item.registration_number) !== itemRegNumber
            )
        )
        setSelectedItems((prevSelectedItems) =>
            prevSelectedItems.filter(
                (selectedRegNumber) => selectedRegNumber !== itemRegNumber
            )
        )
    }

    // Обновление договоров из ЕИС --- НАЧАЛО --------------------------------------------------------

    const handleDeleteUpdateItem = (itemRegNumber: string) => {
        setUpdateItems((prevItems) =>
            prevItems.filter(
                (item) => String(item.registration_number) !== itemRegNumber
            )
        )
        setSelectedItems((prevSelectedItems) =>
            prevSelectedItems.filter(
                (selectedRegNumber) => selectedRegNumber !== itemRegNumber
            )
        )
    }

    const handleUpdatePurchasePopup = () => {
        if (updatePurchasePopup == false) {
            const filteredData = items.filter((item) =>
                selectedItems.includes(String(item.registration_number))
            )
            setUpdateItems(filteredData)
        }
        setUpdatePurchasePopup(!updatePurchasePopup)
        setRowLoadStatus({})
        setRowLoadError({})
    }

    const updatePerformanceFromEis = async (
        purchase_reg_number: string,
        signal?: AbortSignal
    ): Promise<{
        ok: boolean
        purchase?: PurchaseDataReestr
        error?: string
        return_to_ready?: boolean
    }> => {
        setLoading(true)

        const url =
            process.env.REACT_APP_API_URL +
            `/api/backend/v1/purchases/from_eis?reg_number=${purchase_reg_number}`

        try {
            const response = await fetch(url, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                signal,
            })

            // --- обработка ошибок ---
            let errorData: any = null
            try {
                errorData = await response.json()
            } catch (e) {
                console.error('Failed to parse JSON from response:', e)
            }

            const detailMsg: string =
                (errorData && (errorData.detail || errorData.message)) ||
                `Unexpected error. Status: ${response.status}`

            const change_status: boolean = detailMsg.includes(
                'Подготовка к размещению'
            )
                ? true
                : false

            // Если не ОК по HTTP, но и без message — тоже как ошибка
            if (!response.ok) {
                setLoading(false)
                return {
                    ok: false,
                    error: detailMsg,
                    return_to_ready: change_status,
                }
            }

            // Успех — вернули само исполнение
            setLoading(false)
            return {
                ok: true,
                error: `Обновлено`,
            }
        } catch (e: any) {
            // Закрыть — это не ошибка бизнеса, вернем понятное сообщение
            if (e?.name === 'AbortError') {
                setLoading(false)
                return { ok: false, error: 'Операция отменена' }
            }
            setLoading(false)
            return { ok: false, error: String(e) }
        } finally {
            setLoading(false)
        }
    }

    const handleUpdatePurchaseClick = async () => {
        if (!updateItems?.length) return

        setLoading(true)
        const controller = new AbortController()
        abortImportRef.current = controller

        try {
            // Последовательно. При желании можно сделать параллель с семафором.
            for (const row of updateItems) {
                if (controller.signal.aborted) break

                const key = String(row.registration_number)
                setRowLoadStatus((prev) => ({ ...prev, [key]: 'pending' }))
                setRowLoadError((prev) => ({ ...prev, [key]: undefined }))

                const res = await updatePerformanceFromEis(
                    key,
                    controller.signal
                )

                if (res.ok) {
                    setRowLoadStatus((prev) => ({
                        ...prev,
                        [key]: 'success',
                    }))
                } else if (res.return_to_ready == true) {
                    setRowLoadStatus((prev) => ({
                        ...prev,
                        [key]: 'error_back',
                    }))
                    setRowLoadError((prev) => ({ ...prev, [key]: res.error }))
                } else {
                    setRowLoadStatus((prev) => ({ ...prev, [key]: 'error' }))
                    setRowLoadError((prev) => ({ ...prev, [key]: res.error }))
                }
            }
        } finally {
            setLoading(false)
            abortImportRef.current = null
            loadPurchases(1, filter)
        }
    }

    // Обновление исполнения из ЕИС --- КОНЕЦ --------------------------------------------------------

    return (
        <div className="h-full flex flex-col bg-[#f0f3f5] dark:bg-[#181818]">
            <div className="bg-[#e5e5e5] dark:bg-[#111111]">
                <PurchaseHeader
                    theme={theme}
                    yearPeriod={yearPeriod}
                    onYearPeriodChange={setYearPeriod}
                    purchaseCountAndSum={purchaseCountAndSum}
                    loadingCountSum={loadingCountSum}
                />
                <PurchaseStageTab
                    stage={stage}
                    edit={editStage}
                    ready={readyStage}
                    send={sendStage}
                    perf={perfStage}
                    close={closeStage}
                    all={allStage}
                    onTabChange={(newStage) => {
                        setStage(newStage)
                    }}
                    onTabClick={(selectAllFlag) => setSelectAll(selectAllFlag)}
                    theme={theme}
                />
            </div>

            {/* // body */}
            <div className="plan-positions flex p-5 h-[calc(100vh_-_96px_-_28px)]">
                {/* table */}
                <div
                    id="printable-content"
                    className="flex flex-col w-full rounded bg-[#e4e6e8] dark:bg-[#1D1C1E]"
                >
                    {/* table toolbar */}
                    <div className="flex flex-row gap-2 p-2 h-10 rounded-t text-[#181818] dark:text-[#FFFFFF] bg-[#d2d5d8] dark:bg-[#333033]">
                        <HoverTooltip tooltipText="Выделить все">
                            <div className="w-6 h-6 grid rounded-full place-items-center hover:bg-[#FFFFFF] dark:hover:bg-[#656365]">
                                <input
                                    type="checkbox"
                                    className="p-2"
                                    checked={selectAll}
                                    onChange={handleSelectAllChange}
                                />
                            </div>
                        </HoverTooltip>

                        <TableStageToolbarButtons
                            role={isUserRoleName}
                            stage={stage}
                            isSelect={selectedItems.length > 0}
                            isImportingFromEis={isImportingFromEis}
                            multiSelect={selectedItems.length == 1}
                            filter={filter}
                            theme={theme}
                            actions={{
                                import: handleImportFromEisPopup,
                                update: handleUpdatePurchasePopup,
                                delete: handleDeletePurchasePopup,
                                filter: handleFilter,
                            }}
                            // status = {}
                            // multiSelect = {selectedItems.length > 0}
                        />
                    </div>

                    {/* table header (name) */}

                    <TableHeader
                        stage={stage}
                        hasScroll={hasScroll}
                        filter={filter}
                        theme={theme}
                        onFilterErase={() => {
                            handleFilterErase()
                        }}
                        onAcceptFilter={() => {
                            handleFilterChange()
                        }}
                        // Фильтрация для "Реестровый номер"
                        onChangeFilterSettingCriteriaPurchaseRegNumber={(
                            value
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        registration_number: {
                                            ...filter.filter_settings.criteria
                                                ?.registration_number,
                                            query: value,
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)
                        }}
                        onChangeFilterSettingCriteriaPurchaseRegNumberSort={(
                            value
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        registration_number: {
                                            ...filter.filter_settings.criteria
                                                ?.registration_number,
                                            sort_order: value, // Обновляем только сортировку
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)
                        }}
                        // Фильтрация для "Статус закупки"
                        onChangeFilterSettingCriteriaPurchaseStatus={(
                            values
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        purchase_status: {
                                            ...filter.filter_settings.criteria
                                                ?.purchase_status,
                                            values:
                                                values === null ? null : values, // если values равно null, передаем undefined
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)
                        }}
                        onChangeFilterSettingCriteriaPurchaseStatusSort={(
                            value
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        purchase_status: {
                                            ...filter.filter_settings.criteria
                                                ?.purchase_status,
                                            sort_order: value, // Обновляем только сортировку
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)
                        }}
                        // Фильтрация для "Наименование закупки"
                        onChangeFilterSettingCriteriaPurchaseName={(value) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        name: {
                                            ...filter.filter_settings.criteria
                                                ?.name,
                                            query: value,
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)
                        }}
                        onChangeFilterSettingCriteriaPurchaseNameSort={(
                            value
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        name: {
                                            ...filter.filter_settings.criteria
                                                ?.name,
                                            sort_order: value, // Обновляем только сортировку
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)
                        }}
                        // Фильтрация для "Сумма лотов"
                        onChangeFilterSettingCriteriaPurchaseLotSum={(
                            valueMin,
                            valueMax
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        lot_sum: {
                                            ...filter.filter_settings.criteria
                                                ?.lot_sum,
                                            min: valueMin,
                                            max: valueMax,
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)
                        }}
                        onChangeFilterSettingCriteriaPurchaseLotSumSort={(
                            value
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        lot_sum: {
                                            ...filter.filter_settings.criteria
                                                ?.lot_sum,
                                            sort_order: value, // Обновляем только сортировку
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)
                        }}
                        // Фильтрация для "Способ закупки "
                        onChangeFilterSettingCriteriaPurchaseMethodCode={(
                            values
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        purchase_method_code: {
                                            ...filter.filter_settings.criteria
                                                ?.purchase_method_code,
                                            values:
                                                values === null ? null : values, // если values равно null, передаем undefined
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)
                        }}
                        onChangeFilterSettingCriteriaPurchaseMethodCodeSort={(
                            value
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        purchase_method_code: {
                                            ...filter.filter_settings.criteria
                                                ?.purchase_method_code,
                                            sort_order: value, // Обновляем только сортировку
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)
                        }}
                        // Фильтрация для "Дата публикации"
                        onChangeFilterSettingCriteriaPurchasePublicationDateTime={(
                            valueFrom,
                            valueTo
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        publication_date_time: {
                                            ...filter.filter_settings.criteria
                                                ?.publication_date_time,
                                            from: valueFrom,
                                            to: valueTo,
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)
                        }}
                        onChangeFilterSettingCriteriaPurchasePublicationDateTimeSort={(
                            value
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        publication_date_time: {
                                            ...filter.filter_settings.criteria
                                                ?.publication_date_time,
                                            sort_order: value, // Обновляем только сортировку
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)
                        }}
                        // Фильтрация для "Дата начала подачи заявок"
                        onChangeFilterSettingCriteriaPurchaseApplSubmisionStartDate={(
                            valueFrom,
                            valueTo
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        appl_submision_start_date: {
                                            ...filter.filter_settings.criteria
                                                ?.appl_submision_start_date,
                                            from: valueFrom,
                                            to: valueTo,
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)
                        }}
                        onChangeFilterSettingCriteriaPurchaseApplSubmisionStartDateSort={(
                            value
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        appl_submision_start_date: {
                                            ...filter.filter_settings.criteria
                                                ?.appl_submision_start_date,
                                            sort_order: value, // Обновляем только сортировку
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)
                        }}
                        // Фильтрация для "Дата окончания подачи заявок"
                        onChangeFilterSettingCriteriaPurchaseSubmissionCloseDateTime={(
                            valueFrom,
                            valueTo
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        submission_close_date_time: {
                                            ...filter.filter_settings.criteria
                                                ?.submission_close_date_time,
                                            from: valueFrom,
                                            to: valueTo,
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)
                        }}
                        onChangeFilterSettingCriteriaPurchaseSubmissionCloseDateTimeSort={(
                            value
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        submission_close_date_time: {
                                            ...filter.filter_settings.criteria
                                                ?.submission_close_date_time,
                                            sort_order: value, // Обновляем только сортировку
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)
                        }}
                        // Фильтрация для "Дата подведения итогов"
                        onChangeFilterSettingCriteriaPurchaseSummingupDate={(
                            valueFrom,
                            valueTo
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        summingup_date_time: {
                                            ...filter.filter_settings.criteria
                                                ?.summingup_date_time,
                                            from: valueFrom,
                                            to: valueTo,
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)
                        }}
                        onChangeFilterSettingCriteriaPurchaseSummingupDateSort={(
                            value
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        summingup_date_time: {
                                            ...filter.filter_settings.criteria
                                                ?.summingup_date_time,
                                            sort_order: value, // Обновляем только сортировку
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)
                        }}
                        // Фильтрация для "Дата изменения"
                        onChangeFilterSettingCriteriaCreatedDate={(
                            valueFrom,
                            valueTo
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        sys_updated_at: {
                                            ...filter.filter_settings.criteria
                                                ?.sys_updated_at,
                                            from: valueFrom,
                                            to: valueTo,
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)
                        }}
                        onChangeFilterSettingCriteriaCreatedDateSort={(
                            value
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        sys_updated_at: {
                                            ...filter.filter_settings.criteria
                                                ?.sys_updated_at,
                                            sort_order: value, // Обновляем только сортировку
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)
                        }}
                        // Фильтрация для "Пользователь"
                        onChangeFilterSettingCriteriaSupervisorUserGuid={(
                            values
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        supervisor_user_guid: {
                                            ...filter.filter_settings.criteria
                                                ?.supervisor_user_guid,
                                            values:
                                                values === null ? null : values, // если values равно null, передаем undefined
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)
                        }}
                        onChangeFilterSettingCriteriaSupervisorUserGuidSort={(
                            value
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        supervisor_user_guid: {
                                            ...filter.filter_settings.criteria
                                                ?.supervisor_user_guid,
                                            sort_order: value, // Обновляем только сортировку
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)
                        }}
                    />

                    {/* table data */}
                    <div
                        ref={scrollRef}
                        className="flex flex-col gap-1 my-1 h-dvh overflow-y-auto custom-scrollbar"
                        onScroll={(e) => {
                            const { scrollTop, scrollHeight, clientHeight } =
                                e.currentTarget

                            if (
                                page !== -1 && // ещё есть данные
                                !isLoading &&
                                scrollHeight - scrollTop - clientHeight < 100
                            ) {
                                if (filter?.filter_settings?.filter_enabled) {
                                    loadPurchases(page, filter)
                                } else {
                                    loadPurchases(page)
                                }
                            }
                        }}
                        onContextMenu={handleContextMenu} // Обрабатываем правый клик
                    >
                        {/* Вывод позиций */}
                        <>
                            {isLoading && page === 1 ? (
                                <div className="flex w-full h-full items-center justify-center">
                                    <LoadingIcon />
                                </div>
                            ) : !items.length ? (
                                <div className="flex w-full h-12 items-center justify-center text-[#6A6A6A] dark:text-[#8A8A8A] text-[11px]">
                                    Нет данных
                                </div>
                            ) : (
                                items.map((item, index) => (
                                    <PurchasesTableRow
                                        key={index}
                                        stage={stage}
                                        keyindex={index + 1}
                                        purchase={item}
                                        structData={structureList}
                                        selected={selectedItems.includes(
                                            String(item.registration_number)
                                        )}
                                        onClick={(purchase_reg_number) => {
                                            handleCheckboxChange(
                                                purchase_reg_number
                                            )
                                        }}
                                        onDoubleClick={(
                                            purchase_reg_number
                                        ) => {
                                            navigate(
                                                `/hub/purchase/${purchase_reg_number}?tab=Карточка закупки`,
                                                {
                                                    state: {
                                                        purchaseRegNumber:
                                                            purchase_reg_number,
                                                        fromStage:
                                                            new URLSearchParams(
                                                                location.search
                                                            ).get('stage'),
                                                        fromYear: yearPeriod,
                                                    },
                                                }
                                            )
                                        }}
                                        onRightClick={(purchase_reg_number) => {
                                            handleRightClickCheckboxChange(
                                                purchase_reg_number
                                            )
                                        }}
                                        theme={theme}
                                        currency={currencyList}
                                    />
                                ))
                            )}
                            {isLoading && page > 1 && items.length > 0 && (
                                <div className="flex w-full items-center justify-center">
                                    <LoadingIcon />
                                </div>
                            )}
                        </>
                    </div>

                    {/* table buttons */}
                    <div className="flex flex-row gap-3 p-2 px-[22px] rounded-b h-10 text-[#181818] dark:text-[#FFFFFF] bg-[#d2d5d8] dark:bg-[#333033]">
                        <TableStageToolbarDownButtons
                            role={isUserRoleName}
                            stage={stage}
                            isSelect={selectedItems.length > 0}
                            isImportingFromEis={isImportingFromEis}
                            multiSelect={selectedItems.length == 1}
                            theme={theme}
                            actions={{
                                import: handleImportFromEisPopup,
                                update: handleUpdatePurchasePopup,
                                delete: handleDeletePurchasePopup,
                            }}
                            // status = {}
                            // multiSelect = {selectedItems.length > 0}
                        />
                    </div>
                </div>
            </div>

            {importFromEisPopup && (
                <div className="fixed top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center">
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-[700px] max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-row gap-2 items-center justify-left">
                            <h2 className="text-[#181818] dark:text-[#FFFFFF]">
                                Импорт закупок из ЕИС
                            </h2>
                        </div>
                        <div className="flex flex-row w-full gap-3 items-start justify-start pt-2">
                            <Checkbox
                                label="Массовый импорт закупок"
                                checked={massImportMode || false}
                                onChange={() => {
                                    setMassImportMode(!massImportMode)
                                }}
                                disabled={false}
                            />
                        </div>

                        {massImportMode ? (
                            <>
                                <div className="flex flex-row w-full gap-3 items-end justify-end pt-2">
                                    <div className="flex flex-col w-full gap-1 items-start justify-start">
                                        <div className="flex flex-col w-full items-start justify-start text-[12.8px] text-[#6A6A6A] dark:text-[#8A8A8A]">
                                            Начало периода
                                        </div>
                                        <div className="flex w-full items-start justify-start">
                                            <input
                                                type="date"
                                                style={{
                                                    colorScheme:
                                                        theme == 'dark'
                                                            ? 'dark'
                                                            : 'light',
                                                }}
                                                className="form-input w-full bg-[#1F1E1F]"
                                                value={searchFromDate}
                                                onChange={(e) =>
                                                    setSearchFromDate(
                                                        e.target.value
                                                    )
                                                }
                                            ></input>
                                        </div>
                                    </div>
                                    <div className="flex flex-col w-full gap-1 items-start justify-start">
                                        <div className="flex flex-col w-full items-start justify-start text-[12.8px] text-[#6A6A6A] dark:text-[#8A8A8A] ">
                                            Конец периода
                                        </div>
                                        <div className="flex w-full items-start justify-start">
                                            <input
                                                type="date"
                                                style={{
                                                    colorScheme:
                                                        theme == 'dark'
                                                            ? 'dark'
                                                            : 'light',
                                                }}
                                                className="form-input w-full bg-[#1F1E1F]"
                                                value={searchToDate}
                                                onChange={(e) =>
                                                    setSearchToDate(
                                                        e.target.value
                                                    )
                                                }
                                            ></input>
                                        </div>
                                    </div>
                                    <button
                                        className={`flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] text-xs ml-2 items-center justify-center
                                        ${
                                            searchFromDate && searchToDate
                                                ? !isSearchingPurchaseFromEis
                                                    ? 'shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]'
                                                    : 'dark:text-[#8A8A8A] text-[#6a6a6a] bg-[#ededed] dark:bg-[#494849]'
                                                : 'dark:text-[#8A8A8A] text-[#6a6a6a] bg-[#ededed] dark:bg-[#494849]'
                                        }
                                        `}
                                        disabled={
                                            searchFromDate && searchToDate
                                                ? isSearchingPurchaseFromEis
                                                    ? true
                                                    : false
                                                : true
                                        }
                                        onClick={() => {
                                            const controller =
                                                new AbortController()
                                            importPopupAbortRef.current =
                                                controller

                                            searchPurchaseFromEis(
                                                searchFromDate,
                                                searchToDate,
                                                controller.signal
                                            )
                                        }}
                                    >
                                        {isSearchingPurchaseFromEis ? (
                                            <svg
                                                className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <circle
                                                    className="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                ></circle>
                                                <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                                ></path>
                                            </svg>
                                        ) : (
                                            'Поиск'
                                        )}
                                    </button>
                                </div>
                                <div className="flex flex-col w-full pt-2">
                                    {/* name */}
                                    <div className="flex flex-row gap-1 px-2 py-2 rounded-t-lg text-[10px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033]">
                                        <div className="flex w-10 items-center justify-center text-center">
                                            №
                                        </div>
                                        <div className="flex w-24 items-center justify-center text-center">
                                            Реестровый номер
                                        </div>
                                        <div className="flex flex-1 w-20 items-center justify-center text-center">
                                            Объект закупки
                                        </div>
                                        <div className="flex w-28 items-center justify-center text-center">
                                            Сумма
                                        </div>
                                        <div className="flex w-28 items-center justify-center text-center">
                                            Дата размещения
                                        </div>
                                        <div className="flex w-7 items-center justify-center"></div>
                                    </div>
                                    {/* data */}
                                    <div className="flex flex-col h-[300px] gap-1 py-1 px-1 grow rounded-b-lg overflow-y-auto custom-scrollbar bg-[#e4e6e8] dark:bg-[#1D1C1E]">
                                        <div className="flex flex-col w-full space-y-1">
                                            {isSearchingPurchaseFromEis ? (
                                                <div className="flex flex-row rounded text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] hover:bg-[#FFFFFF] dark:bg-[#2A282B] dark:hover:bg-[#373538]">
                                                    <div className="flex flex-row w-full text-[11px] min-h-8 gap-1 items-center justify-center">
                                                        <svg
                                                            className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <circle
                                                                className="opacity-25"
                                                                cx="12"
                                                                cy="12"
                                                                r="10"
                                                                stroke="currentColor"
                                                                strokeWidth="4"
                                                            ></circle>
                                                            <path
                                                                className="opacity-75"
                                                                fill="currentColor"
                                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                                            ></path>
                                                        </svg>
                                                        <span>Поиск...</span>
                                                    </div>
                                                </div>
                                            ) : serachPurchaseFromEisData?.purchase_count !=
                                              0 ? (
                                                serachPurchaseFromEisData?.data.map(
                                                    (item, index) => (
                                                        <div
                                                            key={index}
                                                            className="flex flex-row rounded text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] hover:bg-[#FFFFFF] dark:bg-[#2A282B] dark:hover:bg-[#373538]"
                                                        >
                                                            <div className="flex flex-row w-full p-1">
                                                                <div className="flex flex-row min-h-8 flex-1 gap-1 text-[#181818] dark:text-[#FFFFFF] text-[11px]">
                                                                    <div className="flex w-10 items-center justify-center text-[#6A6A6A] dark:text-[#8A8A8A]">
                                                                        {index +
                                                                            1}
                                                                    </div>
                                                                    <div className="flex w-24 items-center justify-center">
                                                                        {
                                                                            item.purchase_reg_number
                                                                        }
                                                                    </div>
                                                                    <div className="flex flex-1 w-20 items-center justify-start text-left">
                                                                        <HoverTooltip
                                                                            tooltipText={
                                                                                item.purchase_name
                                                                                    ? item.purchase_name
                                                                                    : '-'
                                                                            }
                                                                        >
                                                                            <p className="fourrow">
                                                                                {
                                                                                    item.purchase_name
                                                                                }
                                                                            </p>
                                                                        </HoverTooltip>
                                                                    </div>
                                                                    <div className="flex w-28 items-center justify-end">
                                                                        {item.price_currency ==
                                                                        '₽'
                                                                            ? Number(
                                                                                  item.price_amount
                                                                              ).toLocaleString(
                                                                                  'ru-RU',
                                                                                  {
                                                                                      minimumFractionDigits: 2,
                                                                                  }
                                                                              )
                                                                            : Number(
                                                                                  item.price_amount
                                                                              ).toLocaleString(
                                                                                  'ru-RU',
                                                                                  {
                                                                                      minimumFractionDigits: 2,
                                                                                  }
                                                                              ) +
                                                                              ' ' +
                                                                              item.price_currency}
                                                                    </div>
                                                                    <div className="flex w-28 items-center justify-center">
                                                                        {item.published_date
                                                                            ?.split(
                                                                                '-'
                                                                            )
                                                                            .reverse()
                                                                            .join(
                                                                                '-'
                                                                            )
                                                                            .replace(
                                                                                /[\-]/g,
                                                                                '.'
                                                                            )}
                                                                    </div>
                                                                    <div className="flex w-7 items-center justify-center text-[#6A6A6A] dark:text-[#8A8A8A] ">
                                                                        {(() => {
                                                                            const key =
                                                                                String(
                                                                                    item.purchase_reg_number
                                                                                )
                                                                            const st =
                                                                                rowLoadStatus[
                                                                                    key
                                                                                ]

                                                                            if (
                                                                                st ===
                                                                                'pending'
                                                                            ) {
                                                                                return (
                                                                                    <svg
                                                                                        className="animate-spin h-4 w-4"
                                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                                        fill="none"
                                                                                        viewBox="0 0 24 24"
                                                                                    >
                                                                                        <circle
                                                                                            className="opacity-25"
                                                                                            cx="12"
                                                                                            cy="12"
                                                                                            r="10"
                                                                                            stroke="currentColor"
                                                                                            strokeWidth="4"
                                                                                        ></circle>
                                                                                        <path
                                                                                            className="opacity-75"
                                                                                            fill="currentColor"
                                                                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                                                                        ></path>
                                                                                    </svg>
                                                                                )
                                                                            }

                                                                            if (
                                                                                st ===
                                                                                'success'
                                                                            ) {
                                                                                return (
                                                                                    <svg
                                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                                        className="h-4 w-4 text-green-600 dark:text-green-400"
                                                                                        viewBox="0 0 20 20"
                                                                                        fill="currentColor"
                                                                                        aria-label="Импортирован"
                                                                                    >
                                                                                        <path
                                                                                            fillRule="evenodd"
                                                                                            d="M18.71,7.21a1,1,0,0,0-1.42,0L9.84,14.67,6.71,11.53A1,1,0,1,0,5.29,13l3.84,3.84a1,1,0,0,0,1.42,0l8.16-8.16A1,1,0,0,0,18.71,7.21Z"
                                                                                            clipRule="evenodd"
                                                                                        />
                                                                                    </svg>
                                                                                )
                                                                            }

                                                                            if (
                                                                                st ===
                                                                                'error'
                                                                            ) {
                                                                                return (
                                                                                    <HoverTooltip
                                                                                        tooltipText={
                                                                                            rowLoadError[
                                                                                                key
                                                                                            ] ||
                                                                                            'Ошибка'
                                                                                        }
                                                                                        align="left"
                                                                                    >
                                                                                        <div>
                                                                                            <svg
                                                                                                xmlns="http://www.w3.org/2000/svg"
                                                                                                className="h-4 w-4 text-red-600 dark:text-red-400"
                                                                                                viewBox="0 0 20 20"
                                                                                                fill="currentColor"
                                                                                                aria-label="Ошибка"
                                                                                            >
                                                                                                <path
                                                                                                    fillRule="evenodd"
                                                                                                    d="M13.41,12l4.3-4.29a1,1,0,1,0-1.42-1.42L12,10.59,7.71,6.29A1,1,0,0,0,6.29,7.71L10.59,12l-4.3,4.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l4.29,4.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Z"
                                                                                                    clipRule="evenodd"
                                                                                                />
                                                                                            </svg>
                                                                                        </div>
                                                                                    </HoverTooltip>
                                                                                )
                                                                            }

                                                                            // idle
                                                                            return null
                                                                        })()}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )
                                                )
                                            ) : (
                                                <div className="flex flex-row rounded text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] hover:bg-[#FFFFFF] dark:bg-[#2A282B] dark:hover:bg-[#373538]">
                                                    <div className="flex flex-row w-full text-[11px] min-h-8 items-center justify-center">
                                                        Нет данных
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex flex-row mt-5 text-right justify-end">
                                    <button
                                        className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                        onClick={handleImportFromEisPopup}
                                    >
                                        Закрыть
                                    </button>

                                    {!clickUpdateButton ? (
                                        <button
                                            className={`flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] text-xs  ml-2 items-center justify-center 
                                            ${
                                                serachPurchaseFromEisData &&
                                                serachPurchaseFromEisData?.data
                                                    .length > 0
                                                    ? 'shadow-md shadow-[#18181830] dark:text-[#FFFFFF] selection:text-[#181818] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]'
                                                    : 'dark:text-[#8A8A8A] text-[#6a6a6a] bg-[#ededed] dark:bg-[#494849]'
                                            } `}
                                            disabled={
                                                !(
                                                    serachPurchaseFromEisData &&
                                                    serachPurchaseFromEisData
                                                        ?.data.length > 0
                                                )
                                            }
                                            onClick={importAllFoundPurchases}
                                        >
                                            {isImportingFromEis ? (
                                                <svg
                                                    className="animate-spin h-4 w-4 shadow-md shadow-[#18181830] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40] text-[#181818] dark:text-[#FFFFFF]"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <circle
                                                        className="opacity-25"
                                                        cx="12"
                                                        cy="12"
                                                        r="10"
                                                        stroke="currentColor"
                                                        strokeWidth="4"
                                                    ></circle>
                                                    <path
                                                        className="opacity-75"
                                                        fill="currentColor"
                                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                                    ></path>
                                                </svg>
                                            ) : (
                                                'Добавить'
                                            )}
                                        </button>
                                    ) : (
                                        ''
                                    )}
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="flex flex-col w-full gap-3 items-center justify-center pt-2">
                                    <div className="flex flex-col w-full gap-1 items-start justify-start">
                                        <div className="flex flex-col w-full items-start justify-start text-[12.8px] text-[#6A6A6A] dark:text-[#8A8A8A]">
                                            Реестровый номер закупки
                                        </div>
                                        <div className="flex flex-row w-full items-center justify-center">
                                            <div className="flex w-full items-start justify-start">
                                                <input
                                                    type="text"
                                                    style={{
                                                        colorScheme:
                                                            theme == 'dark'
                                                                ? 'dark'
                                                                : 'light',
                                                    }}
                                                    className="form-input w-full bg-[#1F1E1F]"
                                                    value={purchaseRegNumber}
                                                    onChange={(e) =>
                                                        setPurchaseRegNumber(
                                                            e.target.value
                                                        )
                                                    }
                                                ></input>
                                            </div>
                                            <div className="flex w-6 items-center justify-center text-[#6A6A6A] dark:text-[#8A8A8A] ">
                                                {(() => {
                                                    if (
                                                        isSinglePurchaseImporting
                                                    ) {
                                                        return (
                                                            <svg
                                                                className="animate-spin h-4 w-4"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                fill="none"
                                                                viewBox="0 0 24 24"
                                                            >
                                                                <circle
                                                                    className="opacity-25"
                                                                    cx="12"
                                                                    cy="12"
                                                                    r="10"
                                                                    stroke="currentColor"
                                                                    strokeWidth="4"
                                                                ></circle>
                                                                <path
                                                                    className="opacity-75"
                                                                    fill="currentColor"
                                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                                                ></path>
                                                            </svg>
                                                        )
                                                    }

                                                    if (
                                                        singlePurchaseImportStatus ===
                                                        true
                                                    ) {
                                                        return (
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                className="h-4 w-4 text-green-600 dark:text-green-400"
                                                                viewBox="0 0 20 20"
                                                                fill="currentColor"
                                                                aria-label="Импортирован"
                                                            >
                                                                <path
                                                                    fillRule="evenodd"
                                                                    d="M18.71,7.21a1,1,0,0,0-1.42,0L9.84,14.67,6.71,11.53A1,1,0,1,0,5.29,13l3.84,3.84a1,1,0,0,0,1.42,0l8.16-8.16A1,1,0,0,0,18.71,7.21Z"
                                                                    clipRule="evenodd"
                                                                />
                                                            </svg>
                                                        )
                                                    }

                                                    if (
                                                        singlePurchaseImportStatus ===
                                                        false
                                                    ) {
                                                        return (
                                                            <HoverTooltip
                                                                tooltipText={
                                                                    rowLoadError[
                                                                        purchaseRegNumber
                                                                    ] ||
                                                                    'Ошибка'
                                                                }
                                                                align="left"
                                                            >
                                                                <div>
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        className="h-4 w-4 text-red-600 dark:text-red-400"
                                                                        viewBox="0 0 20 20"
                                                                        fill="currentColor"
                                                                        aria-label="Ошибка"
                                                                    >
                                                                        <path
                                                                            fillRule="evenodd"
                                                                            d="M13.41,12l4.3-4.29a1,1,0,1,0-1.42-1.42L12,10.59,7.71,6.29A1,1,0,0,0,6.29,7.71L10.59,12l-4.3,4.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l4.29,4.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Z"
                                                                            clipRule="evenodd"
                                                                        />
                                                                    </svg>
                                                                </div>
                                                            </HoverTooltip>
                                                        )
                                                    }

                                                    // idle
                                                    return null
                                                })()}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex flex-row w-full mt-5 text-right items-end justify-end">
                                        <button
                                            className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                            onClick={handleImportFromEisPopup}
                                        >
                                            Закрыть
                                        </button>
                                        {!isSinglePurchaseImporting ? (
                                            <button
                                                className={`flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] text-xs ml-2 items-center justify-center ${
                                                    purchaseRegNumber
                                                        ? 'shadow-md shadow-[#18181830] placeholder:text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]'
                                                        : 'dark:text-[#8A8A8A] text-[#6a6a6a] bg-[#ededed] dark:bg-[#494849]'
                                                } `}
                                                disabled={!purchaseRegNumber}
                                                onClick={() => {
                                                    const controller =
                                                        new AbortController()
                                                    importPopupAbortRef.current =
                                                        controller

                                                    importPurchaseFromEis(
                                                        purchaseRegNumber,
                                                        controller.signal
                                                    )
                                                }}
                                            >
                                                Добавить
                                            </button>
                                        ) : (
                                            ''
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
            {/* Контекстное меню */}
            {/* {menuVisible && (
                <div
                    className="absolute flex flex-col w-[200px] shadow-lg rounded-lg p-1 z-10 text-[12.8px] items-center justify-start gap-1 border border-[#b8b8b8] dark:border-[#696769] bg-[#d9dce0] dark:bg-[#333033]"
                    style={{
                        top: menuPosition.y,
                        left: menuPosition.x,
                    }}
                >
                    <TableRightClickButton
                        role={isUserRoleName}
                        stage={stage}
                        isSelect={selectedItems.length > 0}
                        isImportingFromEis={isImportingFromEis}
                        multiSelect={selectedItems.length == 1}
                        theme={theme}
                        actions={{
                            add: () => navigate(`/hub/purchase/new`),
                        }}
                        // status = {}
                        // multiSelect = {selectedItems.length > 0}
                    />
                </div>
            )} */}

            {/* {uploadMessage && (
                <div
                    className="fixed top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center"
                    onClick={() => setUploadMessage(null)}
                >
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-fit max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-col text-sm text-[#181818] dark:text-[#FFFFFF] items-center justify-center gap-5">
                            <div className="flex flex-row text-sm text-[#181818] dark:text-[#FFFFFF] items-center justify-center gap-2">
                                <div
                                    className={`flex w-5 h-5 rounded-full items-center justify-center text-[#181818] dark:text-[#FFFFFF] ${
                                        uploadMessage ==
                                        'Данные успешно импортированы.'
                                            ? 'bg-[#83d2b7] dark:bg-[#187A33]'
                                            : 'bg-[#dc742f]'
                                    }`}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        className="h-4 w-4"
                                    >
                                        <path
                                            fill={
                                                theme == 'dark'
                                                    ? '#FFFFFF'
                                                    : '#181818'
                                            }
                                            d={
                                                uploadMessage ==
                                                'Данные успешно импортированы.'
                                                    ? 'M18.71,7.21a1,1,0,0,0-1.42,0L9.84,14.67,6.71,11.53A1,1,0,1,0,5.29,13l3.84,3.84a1,1,0,0,0,1.42,0l8.16-8.16A1,1,0,0,0,18.71,7.21Z'
                                                    : 'M13.41,12l4.3-4.29a1,1,0,1,0-1.42-1.42L12,10.59,7.71,6.29A1,1,0,0,0,6.29,7.71L10.59,12l-4.3,4.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l4.29,4.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Z'
                                            }
                                        ></path>
                                    </svg>
                                </div>
                                {uploadMessage}
                            </div>
                            <div className="flex w-full flex-row items-center justify-end">
                                <button
                                    className="flex gap-3 px-5 py-1 h-6 min-w-[120px] rounded-[10px] text-[12.8px] items-center justify-center shadow-md shadow-[#18181830] text-xs text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]"
                                    onClick={() => setUploadMessage(null)}
                                >
                                    Ок
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )} */}

            {deletePurchasePopup && (
                <div
                    className="fixed top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center"
                    // onClick={handleDeletePurchasePopup}
                >
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-[700px] max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-row w-full items-center justify-stsrt gap-2">
                            <svg
                                className="w-5 h-5"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                id="times-circle"
                            >
                                <path
                                    fill="#C15C5C"
                                    d="M15.71,8.29a1,1,0,0,0-1.42,0L12,10.59,9.71,8.29A1,1,0,0,0,8.29,9.71L10.59,12l-2.3,2.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l2.29,2.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42L13.41,12l2.3-2.29A1,1,0,0,0,15.71,8.29Zm3.36-3.36A10,10,0,1,0,4.93,19.07,10,10,0,1,0,19.07,4.93ZM17.66,17.66A8,8,0,1,1,20,12,7.95,7.95,0,0,1,17.66,17.66Z"
                                ></path>
                            </svg>
                            <h2 className="text-[#181818] dark:text-[#FFFFFF] text-[18px]">
                                Удаление закупки
                            </h2>
                        </div>
                        <div className="flex flex-col mt-2 space-y-1">
                            <label className="text-[14px] text-[#181818] dark:text-[#FFFFFF]">
                                Вы уверены, что хотите удалить закупку? Это
                                действие является необратимым. Продолжить?
                            </label>
                            <div className="flex flex-row w-full space-x-5 pt-2">
                                <div className="flex flex-col w-full">
                                    {/* name */}
                                    <div className="flex flex-row gap-1 px-1 py-2 rounded-t-lg text-[10px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033]">
                                        <div className="flex w-10 items-center justify-center text-center">
                                            №
                                        </div>
                                        <div className="flex w-24 items-center justify-center text-center">
                                            Реестровый номер
                                        </div>
                                        <div className="flex flex-1 items-center justify-center text-center">
                                            Наименование закупки
                                        </div>
                                        <div className="flex w-6 items-center justify-center"></div>
                                    </div>
                                    {/* data */}
                                    <div className="flex flex-col h-[200px] gap-1 py-1 px-1 grow rounded-b-lg overflow-y-auto custom-scrollbar bg-[#e4e6e8] dark:bg-[#1D1C1E]">
                                        <div className="flex flex-col w-full space-y-1">
                                            {deleteItems.map((item, index) => (
                                                <div
                                                    key={item.guid}
                                                    className="flex flex-row rounded text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] hover:bg-[#FFFFFF] dark:bg-[#2A282B] dark:hover:bg-[#373538]"
                                                >
                                                    <div className="flex flex-row w-full">
                                                        <div className="flex flex-row min-h-8 flex-1 gap-1 text-[#181818] dark:text-[#FFFFFF] text-[11px]">
                                                            <div className="flex w-10 items-center justify-center text-[#6A6A6A] dark:text-[#8A8A8A]">
                                                                {index + 1}
                                                            </div>
                                                            <div className="flex w-24 items-center justify-center">
                                                                {
                                                                    item.registration_number
                                                                }
                                                            </div>
                                                            <div className="flex flex-1 items-center justify-left p-1">
                                                                <HoverTooltip
                                                                    tooltipText={
                                                                        item.name
                                                                            ? item.name
                                                                            : '-'
                                                                    }
                                                                >
                                                                    <p className="fourrow">
                                                                        {
                                                                            item.name
                                                                        }
                                                                    </p>
                                                                </HoverTooltip>
                                                            </div>
                                                            <div className="flex w-8 items-center justify-center text-[#6A6A6A] dark:text-[#8A8A8A] ">
                                                                <button
                                                                    onClick={() =>
                                                                        handleDeleteItem(
                                                                            String(
                                                                                item.registration_number
                                                                            )
                                                                        )
                                                                    }
                                                                    className=" flex w-6 h-6 rounded-full items-center justify-center hover:bg-[#ed9cae] dark:hover:bg-[#701F1F]"
                                                                >
                                                                    <svg
                                                                        className="w-3 h-3"
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        viewBox="0 0 24 24"
                                                                        id="trash-alt"
                                                                    >
                                                                        <path
                                                                            fill={
                                                                                theme ==
                                                                                'dark'
                                                                                    ? '#FFFFFF'
                                                                                    : '#181818'
                                                                            }
                                                                            d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                                                                        ></path>
                                                                    </svg>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-row mt-5 text-right justify-end gap-2">
                            <button
                                className={`flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] ml-2 items-center justify-center text-xs shadow-md shadow-[#18181830] text-[#181818] bg-[#ed9cae] hover:bg-[#ffa7ba] dark:bg-[#701F1F] dark:hover:bg-[#9e2c2c] dark:text-[#FFFFFF]`}
                                onClick={handleDeleteClick} // Вызвать handleDelete
                                disabled={isDeleting ? true : false}
                            >
                                {isDeleting ? (
                                    <svg
                                        className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                        ></path>
                                    </svg>
                                ) : (
                                    'Удалить'
                                )}
                            </button>
                            {isDeleting ? (
                                ''
                            ) : (
                                <button
                                    className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={handleDeletePurchasePopup}
                                >
                                    Закрыть
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default PurchaseListPage
